import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import oktaAuth from './oktaAuth';
import { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from 'common/auth/auth_context';
import { CILogger } from 'common/log/log.provider';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { RootState } from 'store';
import { AuthUserType } from 'common/graphql/types';
import { includes, throttle } from 'lodash';
import { isUserLoggedIn } from 'common/auth/auth_route';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { useTranslation } from 'react-i18next';

interface Props {
    children?: ReactNode;
}

const AuthStateMonitor = ({ children }: Props): ReactElement => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation('global');
    const dispatch = useDispatch();

    const isOktaLogin =
        featureFlags.fxIntegration &&
        auth?.activeUser?.userType !== AuthUserType.PATIENT &&
        auth?.activeUser?.authenticationMethod === 'OKTA';

    const checkIfOktaSessionExistsAndSignOutIfNot = useCallback(async () => {
        if (auth.switchingUser) {
            return;
        }

        const oktaSessionExists = await oktaAuthInstance.session.exists();
        if (!oktaSessionExists) {
            CIPageStatus.error(t('network.401error'));
            CILogger.getInstance().info('User signed out because invalid okta token detected.');
            dispatch(AuthActions.logoutRequest());
        }
    }, [auth, oktaAuthInstance, dispatch]);

    const throttledCheckIfOktaSessionExistsAndSignOutIfNot = useMemo(
        () => throttle(checkIfOktaSessionExistsAndSignOutIfNot, 10000),
        [checkIfOktaSessionExistsAndSignOutIfNot]
    );

    const checkIfOktaSessionExistsAndSignInIfYes = useCallback(async () => {
        if (isUserLoggedIn(auth) || includes(window.location.pathname, '/sso/okta') || auth.switchingUser) {
            return;
        }

        try {
            dispatch(AuthActions.setSSOLoading(true));
            const oktaSessionExists = await oktaAuthInstance.session.exists();

            if (oktaSessionExists) {
                await oktaAuthInstance.signInWithRedirect();
            } else {
                dispatch(AuthActions.setSSOLoading(false));
            }
        } catch (e) {
            dispatch(AuthActions.setSSOLoading(false));
            CILogger.getInstance().info('No SSO session detected', e);
        }
    }, [dispatch, oktaAuthInstance, auth]);

    const checkOktaSession = useCallback(() => {
        if (isOktaLogin) {
            throttledCheckIfOktaSessionExistsAndSignOutIfNot();
        } else {
            checkIfOktaSessionExistsAndSignInIfYes();
        }
    }, [isOktaLogin, checkIfOktaSessionExistsAndSignInIfYes, throttledCheckIfOktaSessionExistsAndSignOutIfNot]);

    // verify okta session on page reload
    useEffect(() => {
        if (featureFlags.fxIntegration) {
            checkOktaSession();
        }
    }, [featureFlags.fxIntegration]); // there is no dependency during signing out of user, it is meant only for page reload

    // verify okta session on visibility change
    useEffect(() => {
        if (featureFlags.fxIntegration) {
            const handleVisibilityChange = async () => {
                if (document.visibilityState === 'visible') {
                    checkOktaSession();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [checkOktaSession, featureFlags.fxIntegration]);

    return <>{children}</>;
};

const OktaAuthProvider = ({ children }: Props): ReactElement => {
    const history = useHistory();

    const restoreOriginalUri: RestoreOriginalUriFunction = (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AuthStateMonitor>{children}</AuthStateMonitor>
        </Security>
    );
};

export default OktaAuthProvider;
